import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { reportsGateway } from '../index';
import { formatReportCurrency, formatReportDate } from '../formatting';
import {
  DEFAULT_SORT, NO_FILTERS, ROWS_PER_PAGE_OPTIONS, STATUS_FILTER_OPTIONS, ariaSortFor, filtersActive, nextSort, pageCount, toListQuery,
  type PayslipFilters, type PayslipSort, type PayslipSortField,
} from '../payslipList';
import { formatEventInstant } from '../timeZone';
import { TimeZoneIndicator, TimeZoneSelect } from '../TimeZoneControl';
import { useTimeZoneChoice } from '../useTimeZoneChoice';
import { PAY_METHODS, PAYSLIP_STATUS_NOTE_PREFIX, payMethodLabel, payslipStatusView } from '../payslipStatus';
import { PayslipAvailabilityNotices } from '../PayslipNotices';
import { PayslipStatusBadge } from '../PayslipStatusBadge';
import { DownloadIcon, EyeIcon, FolderIcon, MailIcon, UnavailableAction } from '../UnavailableAction';
import type { EmployeePayslipListResponse, EmployeePayslipPayPeriod } from '../types';
import '../payslips.css';

/** Wait this long after the last keystroke before asking the server to search (each request composes several modules). */
const SEARCH_DEBOUNCE_MS = 350;

/** The dated and timed note under a row's status chip, e.g. "Paid on Feb 16, 2026, 3:42 PM PST", in the viewer's chosen time zone. Only when the server
 * supplied the event instant (and it is a usable one). */
function statusDate(rawStatus: string, statusSince: string | null, zone: string): string | null {
  const status = payslipStatusView(rawStatus).status;
  const when = status ? formatEventInstant(statusSince, zone) : null;
  return status && when ? `${PAYSLIP_STATUS_NOTE_PREFIX[status]} ${when}` : null;
}

/** A column header that sorts the list (EPN-239): a real button, so it works with the keyboard; `aria-sort` and a visible arrow show the current order. */
function SortableHeader({ label, field, sort, onSort, firstHint, secondHint }: {
  label: string; field: PayslipSortField; sort: PayslipSort; onSort: (field: PayslipSortField) => void; firstHint: string; secondHint: string;
}) {
  const direction = ariaSortFor(sort, field);
  const upcoming = nextSort(sort, field);
  return (
    <th scope="col" aria-sort={direction}>
      <button type="button" className={`payslips__sort${direction === 'none' ? '' : ' payslips__sort--active'}`} onClick={() => onSort(field)}
        title={`Sort ${upcoming === `${field}:asc` ? firstHint : secondHint}`}>
        {label}
        <svg className="payslips__sort-icon" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" data-direction={direction}>
          {direction !== 'descending' && <path d="M6 1.5L9.5 5.5h-7z" fill="currentColor" />}
          {direction !== 'ascending' && <path d="M6 10.5L2.5 6.5h7z" fill="currentColor" />}
        </svg>
      </button>
    </th>
  );
}

function payPeriodLabel(run: Pick<EmployeePayslipPayPeriod, 'periodStartDate' | 'periodEndDate' | 'checkDate'>): string {
  return `${formatReportDate(run.periodStartDate)} – ${formatReportDate(run.periodEndDate)} (pay date ${formatReportDate(run.checkDate)})`;
}

/** Employee Payslips — admin list (EPN-73, extended by EPN-236 for Figma S01/S05, made server-driven by EPN-237).
 *
 * One row per employee per Approved payroll run, from `GET /api/reports/employee-payslips`. The SERVER pages, filters
 * and summarises (KPI cards come from its summary), and derives each payslip's status (Approved, Processing, Paid,
 * Voided), pay method (Direct Deposit, Check or "---"), "simulated" flag and failed-payment flag from existing facts.
 * A Paid from a mock provider reads "Paid (simulated)". When the caller cannot read payment facts or reversals the
 * server says so and notices explain that payslips show as Approved. Controls with no data source (Branch, Email,
 * Export) are shown disabled with a reason, never faked. The Employee Name list is built from the pages viewed so
 * far: the server has no company-wide employee listing, so Search is the way to reach anyone else.
 * See docs/employee-payslips-admin.md. */
export function EmployeePayslipsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<EmployeePayslipListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [filters, setFilters] = useState<PayslipFilters>(NO_FILTERS);
  // What is typed in the search box; `filters.search` follows it after a short pause.
  const [searchText, setSearchText] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PayslipSort>(DEFAULT_SORT);
  const { choice: zoneChoice, setChoice: setZoneChoice, resolved: zone } = useTimeZoneChoice();
  const [knownEmployees, setKnownEmployees] = useState<ReadonlyMap<string, string>>(new Map());
  const ids = { payDay: useId(), payPeriod: useId(), from: useId(), to: useId(), search: useId(), employee: useId(), status: useId(), payMethod: useId(), branch: useId(), rows: useId() };
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);

    const query = toListQuery(filters, page, rowsPerPage, sort);
    // Pay Day and Date Range that cannot both hold match nothing: no request is needed.
    const request = query
      ? reportsGateway.listEmployeePayslips(query, controller.signal)
      : Promise.resolve<EmployeePayslipListResponse | null>(null);

    request
      .then((result) => {
        if (controller.signal.aborted) return;
        setData((previous) => result ?? (previous ? { ...previous, items: [], total: 0, page: 1 } : previous));
        if (result) {
          setKnownEmployees((known) => {
            const next = new Map(known);
            for (const item of result.items) next.set(item.employeeId, item.employeeName);
            return next;
          });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(friendlyErrorMessage(err, 'Could not load employee payslips.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filters, page, rowsPerPage, sort, reloadToken]);

  useEffect(() => () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  const active = filtersActive(filters) || searchText.trim() !== '';
  const employeeOptions = useMemo(() => [...knownEmployees.entries()].sort((a, b) => a[1].localeCompare(b[1])), [knownEmployees]);

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = pageCount(total, rowsPerPage);
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * rowsPerPage;

  function setFilter<K extends keyof PayslipFilters>(key: K, value: PayslipFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }

  function onSort(field: PayslipSortField) {
    setSort((current) => nextSort(current, field));
    setPage(1);
  }

  function onSearchChange(value: string) {
    setSearchText(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setFilter('search', value), SEARCH_DEBOUNCE_MS);
  }

  function resetFilters() {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearchText('');
    setFilters(NO_FILTERS);
    setPage(1);
  }

  const loaded = !error && data !== null;
  const summary = data?.summary ?? null;
  const companyEmpty = loaded && data.payPeriods.length === 0 && data.summary.employeesWithPayslips === 0;
  const noResults = loaded && !companyEmpty && total === 0;
  const showTable = loaded && rows.length > 0;
  const controlsDisabled = !loaded || companyEmpty;
  const failedOnPage = rows.filter((row) => row.paymentFailed).length;

  return (
    <section className="card card--wide payslips payslips-list" aria-labelledby="employee-payslips-heading" aria-busy={loading}>
      <div className="section-header">
        <h2 id="employee-payslips-heading">Employee Payslips</h2>
      </div>
      <p className="muted">Payslips are generated from approved payroll runs.</p>
      <div className="payslips__tz">
        <TimeZoneIndicator choice={zoneChoice} />
        <TimeZoneSelect choice={zoneChoice} onChange={setZoneChoice} />
      </div>

      {loading && <p className="muted" role="status">Loading employee payslips…</p>}

      {error && (
        <div className="payslips__error" role="alert">
          <p className="error">{error}</p>
          <button type="button" onClick={() => setReloadToken((n) => n + 1)}>Try again</button>
        </div>
      )}

      {loaded && (
        <>
          <PayslipAvailabilityNotices
            paymentStatusAvailable={data.paymentStatusAvailable}
            reversalsAvailable={data.reversalsAvailable}
            paymentFailedCount={failedOnPage}
            subject="payslips"
          />
          {data.runsTruncated && (
            <p className="payslips__notice no-print" role="note" data-notice="runs-truncated">
              Only the most recent approved pay periods are included in this list and its summary.
            </p>
          )}
        </>
      )}

      {loaded && summary && (
        <dl aria-label="Payslip summary" className="payslips__kpis">
          <div className="payslips__kpi payslips__kpi--net">
            <dt>Total Net Pay (YTD)</dt>
            <dd className="payslips__kpi-value">{formatReportCurrency(summary.totalNetPayYtd)}</dd>
            <dd className="payslips__kpi-note">{summary.ytdYear}, excludes voided</dd>
          </div>
          <div className="payslips__kpi payslips__kpi--employees">
            <dt>Employees with Payslips</dt>
            <dd className="payslips__kpi-value">{summary.employeesWithPayslips}</dd>
            <dd className="payslips__kpi-note">Company-wide active count not available here</dd>
          </div>
          <div className="payslips__kpi payslips__kpi--period">
            <dt>Last Pay Period</dt>
            <dd className="payslips__kpi-value payslips__kpi-value--text">
              {summary.lastPeriod
                ? `${formatReportDate(summary.lastPeriod.start)} – ${formatReportDate(summary.lastPeriod.end)}`
                : 'No payroll run yet'}
            </dd>
            {summary.lastPeriod && <dd className="payslips__kpi-note">Pay date {formatReportDate(summary.lastPeriod.checkDate)}</dd>}
          </div>
          <div className="payslips__kpi payslips__kpi--approved">
            <dt>Approved</dt>
            <dd className="payslips__kpi-value">{summary.approvedCount}</dd>
            <dd className="payslips__kpi-note">
              Release tracking not available yet
              {summary.approvedCount > 0 && (
                <>
                  {' · '}
                  <button type="button" className="link payslips__kpi-link" onClick={() => setFilter('status', 'Approved')}>Review</button>
                </>
              )}
            </dd>
          </div>
        </dl>
      )}

      {(loaded || loading) && !error && (
        <div className="payslips__toolbar">
          <div className="payslips__filters" role="search" aria-label="Filter payslips">
            <div className="payslips__field">
              <label htmlFor={ids.payDay}>Pay Day</label>
              <input id={ids.payDay} type="date" disabled={controlsDisabled} value={filters.payDay} onChange={(e) => setFilter('payDay', e.target.value)} />
            </div>
            <div className="payslips__field payslips__field--wide">
              <label htmlFor={ids.payPeriod}>Select Pay Period</label>
              <select id={ids.payPeriod} disabled={controlsDisabled} value={filters.payPeriod} onChange={(e) => setFilter('payPeriod', e.target.value)}>
                <option value="">All pay periods</option>
                {(data?.payPeriods ?? []).map((run) => <option key={run.payrollRunId} value={run.payrollRunId}>{payPeriodLabel(run)}</option>)}
              </select>
            </div>
            <fieldset className="payslips__field payslips__field--range">
              <legend>Date Range (pay date)</legend>
              <div className="payslips__range">
                <label className="visually-hidden" htmlFor={ids.from}>From</label>
                <input id={ids.from} type="date" disabled={controlsDisabled} value={filters.from} max={filters.to || undefined} onChange={(e) => setFilter('from', e.target.value)} />
                <span aria-hidden="true">–</span>
                <label className="visually-hidden" htmlFor={ids.to}>To</label>
                <input id={ids.to} type="date" disabled={controlsDisabled} value={filters.to} min={filters.from || undefined} onChange={(e) => setFilter('to', e.target.value)} />
              </div>
            </fieldset>
          </div>

          <div className="payslips__header-actions">
            <UnavailableAction label="Email payslips" reason="Emailing payslips is not available yet" className="payslips__header-btn">
              <MailIcon /> Email
            </UnavailableAction>
            <UnavailableAction label="Export payslips" reason="Exporting the payslip list is not available yet" className="payslips__header-btn payslips__header-btn--primary">
              <DownloadIcon /> Export
            </UnavailableAction>
          </div>
        </div>
      )}

      {(loaded || loading) && !error && (
        <div className="payslips__filters payslips__filters--secondary" role="group" aria-label="Refine payslips">
          <div className="payslips__field payslips__field--search">
            <label className="visually-hidden" htmlFor={ids.search}>Search</label>
            <input
              id={ids.search}
              className="search-box"
              type="search"
              disabled={controlsDisabled}
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search employee name or number"
            />
          </div>
          <div className="payslips__field">
            <label className="visually-hidden" htmlFor={ids.employee}>Employee Name</label>
            <select id={ids.employee} disabled={controlsDisabled} value={filters.employeeId} onChange={(e) => setFilter('employeeId', e.target.value)}>
              <option value="">Employee Name</option>
              {employeeOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div className="payslips__field">
            <label className="visually-hidden" htmlFor={ids.payMethod}>Pay Method</label>
            <select id={ids.payMethod} disabled={controlsDisabled || !data?.paymentStatusAvailable} value={filters.payMethod} onChange={(e) => setFilter('payMethod', e.target.value)}
              title={data && !data.paymentStatusAvailable ? 'Pay method is not available for your role.' : undefined}>
              <option value="">Pay Method</option>
              {PAY_METHODS.map((method) => <option key={method} value={method}>{payMethodLabel(method)}</option>)}
            </select>
          </div>
          <div className="payslips__field">
            <label className="visually-hidden" htmlFor={ids.status}>Status</label>
            <select id={ids.status} disabled={controlsDisabled} value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">Status</option>
              {STATUS_FILTER_OPTIONS.map((label) => <option key={label} value={label}>{label}</option>)}
            </select>
          </div>
          <div className="payslips__field">
            <label className="visually-hidden" htmlFor={ids.branch}>Branch (not available yet)</label>
            <select id={ids.branch} disabled title="Branch is not available yet: payslips carry no branch data." value="">
              <option value="">Branch</option>
            </select>
          </div>
          {active && <button type="button" className="link" onClick={resetFilters}>Reset filters</button>}
        </div>
      )}

      {loaded && (
        <div className="payslips__bar">
          <label className="payslips__rows" htmlFor={ids.rows}>
            Rows per page{' '}
            <select id={ids.rows} value={rowsPerPage} disabled={companyEmpty} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <span className="muted" role="status" aria-live="polite">
            {showTable
              ? `${firstIndex + 1}-${firstIndex + rows.length} of ${total} ${total === 1 ? 'payslip' : 'payslips'}`
              : `0 of ${total} payslips`}
          </span>
          <nav className="payslips__paging" aria-label="Pagination">
            <button type="button" disabled={currentPage <= 1 || !showTable} onClick={() => setPage(currentPage - 1)}>Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button type="button" disabled={currentPage >= totalPages || !showTable} onClick={() => setPage(currentPage + 1)}>Next</button>
          </nav>
        </div>
      )}

      {loaded && (
        <div className="table-scroll" role="region" aria-label="Employee payslips table" tabIndex={0}>
          <table aria-label="Employee payslips" className="payslips__table">
            <thead>
              <tr>
                <SortableHeader label="Employee Name" field="employeeName" sort={sort} onSort={onSort} firstHint="A to Z" secondHint="Z to A" />
                <th scope="col">Pay Period</th>
                <SortableHeader label="Pay Date" field="checkDate" sort={sort} onSort={onSort} firstHint="oldest first" secondHint="newest first" />
                <th scope="col" className="num">Gross Pay</th>
                <th scope="col" className="num">Net Pay</th>
                <th scope="col">Pay Method</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            {showTable && (
              <tbody>
                {rows.map((row) => {
                  const who = `${row.employeeName}, pay date ${formatReportDate(row.checkDate)}`;
                  return (
                    <tr key={`${row.payrollRunId}-${row.employeeId}`}>
                      <th scope="row">{row.employeeNumber} — {row.employeeName}</th>
                      <td>{formatReportDate(row.periodStartDate)} – {formatReportDate(row.periodEndDate)}</td>
                      <td>{formatReportDate(row.checkDate)}</td>
                      <td className="num">{row.grossPay === null ? '---' : formatReportCurrency(row.grossPay)}</td>
                      <td className="num">{formatReportCurrency(row.netPay)}</td>
                      <td>{payMethodLabel(row.payMethod)}</td>
                      <td>
                        <div className="payslips__status-cell">
                          <PayslipStatusBadge status={row.status} simulated={row.simulated} />
                          {statusDate(row.status, row.statusSince, zone.zone) && <span className="payslips__status-date">{statusDate(row.status, row.statusSince, zone.zone)}</span>}
                          {row.paymentFailed && <span className="payslips__row-failed">Payment failed</span>}
                        </div>
                      </td>
                      <td>
                        <div className="payslips__row-actions">
                          <button
                            type="button"
                            className="payslips__icon-btn"
                            aria-label={`View payslip for ${who}`}
                            title="View payslip"
                            onClick={() => navigate(`/payroll/employee-payslips/${row.payrollRunId}/${row.employeeId}`)}
                          >
                            <EyeIcon />
                          </button>
                          <UnavailableAction label={`Email payslip for ${who}`} reason="Emailing a payslip is not available yet" className="payslips__icon-btn">
                            <MailIcon />
                          </UnavailableAction>
                          <UnavailableAction label={`Download payslip for ${who}`} reason="Downloading a payslip (PDF) is not available yet" className="payslips__icon-btn">
                            <DownloadIcon />
                          </UnavailableAction>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>

          {companyEmpty && (
            <div className="payslips__empty payslips__empty--company">
              <FolderIcon />
              <p><strong>No Data Available</strong></p>
              <p className="muted">Payslip records will appear here once a payroll run is approved.</p>
            </div>
          )}
          {noResults && (
            <div className="payslips__empty payslips__empty--filtered">
              <FolderIcon />
              <p><strong>No payslips match your filters.</strong></p>
              <button type="button" className="link" onClick={resetFilters}>Reset filters</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
